const SESSION_KEY = "gospelgo_cms_session";

export type CmsUser = {
  sub: string;
  email: string | null;
};

export function getStoredSessionToken(): string | null {
  try {
    return localStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}

export function setStoredSessionToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(SESSION_KEY, token);
    else localStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore quota / private mode
  }
}

export function appleClientId(): string {
  return (import.meta.env.VITE_APPLE_CLIENT_ID as string | undefined)?.trim() ?? "";
}

export function appleRedirectUri(): string {
  const configured = (import.meta.env.VITE_APPLE_REDIRECT_URI as string | undefined)?.trim();
  if (configured) return configured;
  return `${window.location.origin}/auth/callback`;
}

export function isAppleClientConfigured(): boolean {
  return Boolean(appleClientId());
}

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (config: {
          clientId: string;
          scope: string;
          redirectURI: string;
          state?: string;
          usePopup?: boolean;
        }) => void;
        signIn: () => Promise<{
          authorization: {
            id_token: string;
            code?: string;
            state?: string;
          };
          user?: {
            email?: string;
            name?: { firstName?: string; lastName?: string };
          };
        }>;
      };
    };
  }
}

let appleScriptPromise: Promise<void> | null = null;

export function loadAppleScript(): Promise<void> {
  if (window.AppleID) return Promise.resolve();
  if (appleScriptPromise) return appleScriptPromise;
  appleScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>("script[data-apple-auth]");
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Failed to load Apple JS.")));
      return;
    }
    const script = document.createElement("script");
    script.src =
      "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
    script.async = true;
    script.dataset.appleAuth = "true";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Apple JS."));
    document.head.appendChild(script);
  });
  return appleScriptPromise;
}

export function describeAppleError(raw: unknown): Error {
  if (raw instanceof Error) {
    const msg = raw.message.toLowerCase();
    if (
      msg.includes("sign up not completed") ||
      msg.includes("setup not complete") ||
      msg.includes("registration not complete")
    ) {
      return new Error(
        "Apple says Sign in with Apple setup is incomplete. In Apple Developer → Identifiers → Services IDs, open your Services ID, enable Sign in with Apple → Configure, pick the primary App ID, set Domains to cms.gospelgo.app (no https), Return URLs to https://cms.gospelgo.app/auth/callback, then Save. Wait a minute and retry.",
      );
    }
    return raw;
  }

  const code =
    typeof raw === "object" && raw !== null && "error" in raw
      ? String((raw as { error: unknown }).error)
      : "";

  const detail =
    typeof raw === "object" && raw !== null && "error_description" in raw
      ? String((raw as { error_description: unknown }).error_description)
      : "";

  const combined = `${code} ${detail}`.toLowerCase();
  if (
    combined.includes("sign up not completed") ||
    combined.includes("setup not complete") ||
    code === "1000"
  ) {
    return new Error(
      "Apple says Sign in with Apple setup is incomplete. Confirm Services ID domains/return URLs are saved (cms.gospelgo.app + https://cms.gospelgo.app/auth/callback) and the primary App ID has Sign in with Apple enabled.",
    );
  }

  switch (code) {
    case "popup_closed_by_user":
    case "user_cancelled_authorize":
      return new Error("Sign-in window was closed before Apple finished.");
    case "invalid_client":
      return new Error(
        "Apple rejected the client. Check that VITE_APPLE_CLIENT_ID exactly matches the Services ID, and that cms.gospelgo.app + the return URL are registered on it.",
      );
    case "invalid_request":
      return new Error(
        "Apple rejected the request. The page origin must match a domain registered on the Services ID (HTTPS, no localhost).",
      );
    default:
      return new Error(code ? `Apple sign-in error: ${code}` : "Apple sign-in failed.");
  }
}

export async function exchangeAppleIdentityToken(
  identityToken: string,
): Promise<{ token: string; user: CmsUser }> {
  const res = await fetch("/api/auth/apple", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ identityToken }),
  });
  const data = (await res.json()) as {
    token?: string;
    user?: CmsUser;
    error?: string;
    appleSub?: string;
  };
  if (!res.ok) {
    const hint = data.appleSub ? ` Apple sub: ${data.appleSub}` : "";
    throw new Error((data.error ?? `Sign-in failed (${res.status})`) + hint);
  }
  if (!data.token || !data.user) throw new Error("Malformed auth response.");
  return { token: data.token, user: data.user };
}

export async function fetchAuthMe(token: string): Promise<CmsUser> {
  const res = await fetch("/api/auth/me", {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = (await res.json()) as { user?: CmsUser; error?: string };
  if (!res.ok) throw new Error(data.error ?? `Session invalid (${res.status})`);
  if (!data.user) throw new Error("Malformed /me response.");
  return data.user;
}
